import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchSearchResult } from '../../shared/api/types';
import { Button } from '../../shared/ui/components';
import { countryName } from '../../shared/geo/places';

type State = { step: 'idle' | 'locating' | 'loading' } | { step: 'denied' | 'error'; message: string } | { step: 'ready'; items: ChurchSearchResult[] };

/** La ubicación viaja redondeada (≈1 km): basta para ordenar por cercanía y no expone dónde está la persona. */
const coarse = (n: number) => Math.round(n * 100) / 100;
const km = (d: number) => (d < 1 ? `${Math.round(d * 1000)} m` : `${d.toLocaleString('es-MX', { maximumFractionDigits: 1 })} km`);

/** «Cerca de mí» (dentro de «Buscar mi iglesia»): pide permiso del GPS y muestra las 3 iglesias más cercanas. */
export function NearbyChurches({ auto = false }: { auto?: boolean }) {
  const [state, setState] = useState<State>({ step: 'idle' });

  function locate() {
    if (!('geolocation' in navigator)) return setState({ step: 'denied', message: 'Tu navegador no permite usar la ubicación. Busca tu iglesia por nombre o ciudad.' });
    setState({ step: 'locating' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({ step: 'loading' });
        api
          .get<{ items: ChurchSearchResult[] }>(`/multichurch/public/churches/nearby?lat=${coarse(pos.coords.latitude)}&lng=${coarse(pos.coords.longitude)}`)
          .then((r) => setState({ step: 'ready', items: r.items }))
          .catch((e) => setState({ step: 'error', message: errorMessage(e) }));
      },
      () => setState({ step: 'denied', message: 'No nos diste permiso de usar tu ubicación. Puedes activarlo en tu navegador o buscar tu iglesia por nombre o ciudad.' }),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  }

  useEffect(() => {
    if (auto) locate();
  }, [auto]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="nearby" aria-label="Iglesias cerca de ti">
      <Button type="button" variant="secondary" busy={state.step === 'locating' || state.step === 'loading'} onClick={locate}>
        📍 Cerca de mí
      </Button>
      {state.step === 'denied' || state.step === 'error' ? <p className="small muted" role="alert">{state.message}</p> : null}
      {state.step === 'ready' && state.items.length === 0 ? <p className="small muted">Todavía no hay iglesias con su ubicación en el mapa cerca de ti. Búscala por nombre o ciudad.</p> : null}
      {state.step === 'ready' && state.items.length > 0 ? (
        <>
          <h2 className="small">Las más cercanas a ti</h2>
          <ul className="church-results">
            {state.items.map((c) => (
              <li key={c.slug}>
                <Link to={`/i/${c.slug}`} className="church-result">
                  {c.logoUrl ? <img src={c.logoUrl} alt="" /> : <span className="church-result__initial" aria-hidden="true">{c.name.charAt(0)}</span>}
                  <span>
                    <strong>{c.name}</strong>
                    <span className="muted small block">
                      {c.distanceKm != null ? <span className="nearby__km">a {km(c.distanceKm)} · </span> : null}
                      {[c.city, c.state, c.country ? countryName(c.country) : null].filter(Boolean).join(', ') || 'Ubicación en el mapa'}
                    </span>
                    {c.tagline ? <span className="small block">{c.tagline}</span> : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
